import { AsyncBoundary } from "@components/AsyncBoundary";
import { PhoneDock } from "@frontdesk/PhoneDock";
import { RoomsBand, RoomsBandSkeleton } from "@frontdesk/RoomsBand";
import { TodosBand, TodosBandSkeleton } from "@frontdesk/TodosBand";
import {
  CalendarIcon,
  ChevronLeftIcon,
  cn,
  EnvelopeIcon,
  PanelError,
  PhoneIcon,
} from "@scribenote/brian-design-system";
import { DisplayFrame } from "@shell/DisplayFrame";
import { GlobalControls } from "@shell/GlobalControls";
import { SearchField, type SearchFieldHandle } from "@shell/SearchField";
import { Link, Outlet, useMatchRoute } from "@tanstack/react-router";
import { useRef, useState, type ReactNode } from "react";

type FrontDeskPath =
  | "/display/front-desk"
  | "/display/front-desk/schedule"
  | "/display/front-desk/calls"
  | "/display/front-desk/email";

const HeaderLink = ({
  to,
  icon,
  label,
}: {
  to: FrontDeskPath;
  icon: ReactNode;
  label: string;
}) => (
  <Link
    to={to}
    activeOptions={{ exact: true, includeSearch: false }}
    data-dd-action-name={`Front desk: ${label}`}
    className={cn(
      "inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-[4px] border-[1.5px] border-rule-strong px-3 py-2",
      "font-mono text-xs font-medium uppercase tracking-[0.12em] text-ink-body",
      "transition duration-150 ease-paper hover:bg-smudge",
      "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
      "data-[status=active]:border-ink data-[status=active]:bg-ink data-[status=active]:text-ink-inverse",
    )}
  >
    <span aria-hidden="true" className="inline-flex text-base">
      {icon}
    </span>
    {label}
  </Link>
);

/** Schedule, Calls, Email and patients replace the bands under one header; the phone dock stays put. */
export const FrontDeskDisplay = () => {
  const matchRoute = useMatchRoute();
  const onHome = Boolean(matchRoute({ to: "/display/front-desk" }));
  const searchRef = useRef<SearchFieldHandle>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  return (
    <DisplayFrame
      tapes={["Front desk"]}
      homeTo="/display/front-desk"
      actions={
        <>
          <div className="w-[26.25rem]">
            <SearchField ref={searchRef} />
          </div>
          <nav
            aria-label="Front desk"
            className="flex items-center justify-end gap-3"
          >
            {!onHome && (
              <HeaderLink
                to="/display/front-desk"
                icon={<ChevronLeftIcon />}
                label="Desk"
              />
            )}
            <HeaderLink
              to="/display/front-desk/schedule"
              icon={<CalendarIcon />}
              label="Schedule"
            />
            <HeaderLink
              to="/display/front-desk/calls"
              icon={<PhoneIcon />}
              label="Calls"
            />
            <HeaderLink
              to="/display/front-desk/email"
              icon={<EnvelopeIcon />}
              label="Email"
            />
          </nav>
        </>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
          <Outlet />
        </div>
        <PhoneDock />
      </div>
      <GlobalControls
        surface="frontDesk"
        onSearch={() => searchRef.current?.focus()}
        shortcutsOpen={shortcutsOpen}
        setShortcutsOpen={setShortcutsOpen}
      />
    </DisplayFrame>
  );
};

export const FrontDeskHome = () => (
  <>
    <AsyncBoundary
      skeleton={<RoomsBandSkeleton />}
      fallback={(retry) => (
        <PanelError
          label="the rooms"
          onRetry={retry}
          className="h-auto shrink-0"
        />
      )}
    >
      <RoomsBand />
    </AsyncBoundary>
    <AsyncBoundary
      skeleton={<TodosBandSkeleton />}
      fallback={(retry) => <PanelError label="the to-dos" onRetry={retry} />}
    >
      <TodosBand />
    </AsyncBoundary>
  </>
);
